import React, { useEffect, useState } from "react";
import { Box, Drawer, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import { scrollCSS } from "../../../App";
import Waveloading from "../../loading/Waveloading";
import {
  ACCENT,
  ACCENT_LIGHT,
  PINK_BORDER,
  drawerPaper,
  drawerHeader,
  drawerIconChip,
} from "../churn-recovery/churnStyles";
import CrmNotes from "./crmCustomerDetail/crmNotes/crmNotes";
import CrmAddNotes from "./crmCustomerDetail/crmNotes/crmAddNotes";

/**
 * Slide-in notes panel for a CRM customer — the same content as the
 * /customer/notes/:user_id page, opened in place so the list, search and
 * pagination behind it survive.
 *
 * One shared instance lives at page level in CRMPage; mounting it per row would
 * fire a notes fetch for every customer in the list.
 */
const CustomerNotesDrawer = ({ open, onClose, customer }) => {
  const customerId = customer?.customer_id;
  const [saving, setSaving] = useState(false);

  // Closing mid-save unmounts the form before it can report back, which would
  // leave a stuck overlay waiting on the next open.
  useEffect(() => {
    if (!open) setSaving(false);
  }, [open]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: drawerPaper }}>
      <Box sx={drawerHeader}>
        <Box sx={drawerIconChip}>
          <StickyNote2OutlinedIcon sx={{ color: ACCENT, fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "17px",
              color: "#1a1a2e",
              lineHeight: 1.2,
              fontFamily: "helvetica-lt-std-bold",
            }}
          >
            Notes
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              color: "#6B7280",
              mt: "2px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {customer?.customer_name}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            width: 32,
            height: 32,
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            color: "#6B7280",
            "&:hover": { background: ACCENT_LIGHT, color: ACCENT },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Single scroll region: the note list and the add form scroll together. */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", ...scrollCSS }}>
        {customerId ? (
          <>
            <CrmNotes customerId={customerId} embedded />
            <Box sx={{ borderTop: `1px solid ${PINK_BORDER}` }}>
              <CrmAddNotes
                customerId={customerId}
                embedded
                onSavingChange={setSaving}
              />
            </Box>
          </>
        ) : null}
      </Box>

      {/* Scoped to this panel: the Drawer Paper is position:fixed, so a `cover`
          overlay anchors to it and leaves the page behind the slider clear. */}
      {saving ? <Waveloading cover /> : null}
    </Drawer>
  );
};

export default CustomerNotesDrawer;
